import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { LoaderSpinner } from "../LoaderSpinner";
import { enumInventoryPhasedStatus } from "../../consts/inventoryStatus";
import {
    ArchiveBoxIcon,
    ArrowDownIcon,
    ArrowPathIcon,
    ArrowTopRightOnSquareIcon,
    ArrowUturnLeftIcon,
    ArrowUpIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CubeIcon,
    ExclamationTriangleIcon,
    QuestionMarkCircleIcon,
    InformationCircleIcon,
    RectangleStackIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { LockClosedIcon } from "@heroicons/react/20/solid";
import {
    blockUnsignedIntegerKeyDown,
    sanitizeUnsignedIntegerString,
} from "../../utils/integerInput";
import { DatePickerInput } from "../DatePickerInput";
import { ConfrimModal } from "../ConfrimModal";
import { ProductCatalogBulkFieldMenu } from "./ProductCatalogBulkFieldMenu";
import { ProductCatalogBulkSectionModals } from "./ProductCatalogBulkSectionModals";
import { formatShopifyGidForDisplay } from "./shopifyDisplay";
import { normalizeCatalogColumns } from "../../consts/productCatalog";
import {
    CatalogInfoTip,
    CATALOG_MFG_LEAD_TIME_INFO,
    CATALOG_PROTECTED_INFO,
    CATALOG_MAX_DISCOUNT_PCT_INFO,
    CATALOG_MAX_DISCOUNT_PRICE_INFO,
    CATALOG_MSRP_INFO,
    CATALOG_SHOPIFY_PLANNED_GROSS_PRICE_INFO,
    CATALOG_AMAZON_PLANNED_GROSS_PRICE_INFO,
    CATALOG_TOTAL_LEAD_TIME_INFO,
} from "./CatalogInfoTip";
import { CatalogStatusChip } from "./CatalogStatusChip";
import SupplySku from "../SupplySku";

function CatalogBulkToolbarPopoverCard({ className = "", children }) {
    return (
        <div
            className={`absolute left-0 top-full z-50 mt-1.5 w-[min(32rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white p-4 shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)] ${className || ""}`}
        >
            {children}
        </div>
    );
}

function CatalogBulkActionButton({ Icon, label, onClick, isOpen = false, disabled = false, danger = false }) {
    const buttonClass = danger
        ? isOpen
            ? "bg-[#991b1b] text-[#ffb4ad] shadow-sm"
            : "bg-[#7f1d1d] text-[#ffb4ad] hover:bg-[#991b1b] hover:text-[#ffb4ad]"
        : isOpen
          ? "bg-[#1f2937] text-white"
          : "bg-[#111827] text-white/90 hover:bg-[#1f2937] hover:text-white";
    const iconClass = danger ? "text-[#ffb4ad]" : "text-current";
    const chevronClass = danger ? "text-[#ffb4ad]" : "opacity-70";
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={[
                "group inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-semibold transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-50",
                buttonClass,
            ].join(" ")}
        >
            <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden />
            <span>{label}</span>
            <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 ${chevronClass}`} aria-hidden />
        </button>
    );
}

function CatalogBulkDeleteConfirm({ count, busy, onCancel, onDelete }) {
    return (
        <CatalogBulkToolbarPopoverCard className="!w-[290px]">
            <div className="mb-3 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-500" aria-hidden />
                <p className="text-sm font-semibold text-gray-900">
                    Delete {count} {count === 1 ? "product" : "products"}?
                </p>
            </div>
            <p className="mb-4 text-xs text-gray-500">
                This will permanently remove the selected product{count === 1 ? "" : "s"} from the catalog.
            </p>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-gray-100 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-red-500 px-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                    onClick={onDelete}
                >
                    Delete
                </button>
            </div>
        </CatalogBulkToolbarPopoverCard>
    );
}

function CatalogBulkRestoreConfirm({ count, busy, onCancel, onRestore }) {
    return (
        <CatalogBulkToolbarPopoverCard className="!w-[300px]">
            <div className="mb-3 flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-900">
                    Restore {count} {count === 1 ? "product" : "products"}?
                </p>
            </div>
            <p className="mb-4 text-xs text-gray-500">
                Selected products will be available again in the main catalog views.
            </p>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-gray-100 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md border border-black bg-black px-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:opacity-50"
                    onClick={onRestore}
                >
                    Restore
                    {busy ? (
                        <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                    ) : null}
                </button>
            </div>
        </CatalogBulkToolbarPopoverCard>
    );
}

const EMPTY_VALUE = "-";
/** Every table body `<td>` uses this padding */
const TD_CELL_PAD = "px-3 py-2";

const parseLeadTimeTableCell = (value) => {
    if (value === null || value === undefined || value === "" || value === EMPTY_VALUE) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const recomputeTotalLeadTimeDays = (row) => {
    const mfg = parseLeadTimeTableCell(row?.lead_time_days);
    const transit = parseLeadTimeTableCell(row?.transit_lead_time_days);
    if (mfg === null && transit === null) return EMPTY_VALUE;
    return (mfg ?? 0) + (transit ?? 0);
};

const formatMfgLeadTimeTableDisplay = (value) => {
    const n = parseLeadTimeTableCell(value);
    if (n === null) return EMPTY_VALUE;
    return `${n} days`;
};

const hasPositiveLeadTime = (value) => {
    const n = parseLeadTimeTableCell(value);
    return n !== null && n > 0;
};

const formatTransitLeadTimeTableDisplay = (value) => {
    if (!hasPositiveLeadTime(value)) return EMPTY_VALUE;
    return `${parseLeadTimeTableCell(value)} days`;
};

const formatTotalLeadTimeTableDisplay = (item) => {
    const mfg = parseLeadTimeTableCell(item?.lead_time_days);
    const transit = parseLeadTimeTableCell(item?.transit_lead_time_days);
    const transitIsKnown = transit !== null && transit > 0;

    if (transitIsKnown) {
        const total = (mfg ?? 0) + transit;
        return `${total} days`;
    }

    if (mfg === null) return EMPTY_VALUE;
    return `${mfg}d + ?`;
};

function renderLeadTimeCellValue(columnId, shownValue) {
    return (
        <div className="flex min-w-0 items-center justify-between gap-1">
            <span className="min-w-0 whitespace-normal break-words">{shownValue}</span>
            {columnId === "lead_time_days" ? (
                <CatalogInfoTip text={CATALOG_MFG_LEAD_TIME_INFO} />
            ) : null}
        </div>
    );
}

/** Luggage (name or category_group_id 3) → FCL container; all others → carton. */
function isLuggageCategory(row) {
    return row?.category === "Luggage" || Number(row?.category_group_id) === 3;
}

function getHistoricalShipMethod(row) {
    if (isLuggageCategory(row)) {
        return {
            method: "Container",
            note: "Luggage products have historically shipped by full container load (FCL). Connect to Inbound Shipments tab for exact shipment history.",
        };
    }
    return {
        method: "Carton",
        note: "This product has historically shipped by carton. Connect to Inbound Shipments tab for exact shipment history.",
    };
}

function ContainerQtyHistoricalHelp({ row, isOpen, onOpen, onClose, scrollContainerRef }) {
    const anchorRef = useRef(null);
    const popoverRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const { method, note } = getHistoricalShipMethod(row);
    const isContainer = method === "Container";

    const updatePosition = useCallback(() => {
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const popoverWidth = 256;
        const gap = 4;
        let left = rect.left;
        if (left + popoverWidth > window.innerWidth - gap) {
            left = Math.max(gap, window.innerWidth - gap - popoverWidth);
        }
        setPos({ top: rect.bottom + gap, left });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return;
        updatePosition();
    }, [isOpen, updatePosition]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleClickOutside = (event) => {
            if (anchorRef.current?.contains(event.target)) return;
            if (popoverRef.current?.contains(event.target)) return;
            onClose();
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") onClose();
        };
        const handleReposition = () => updatePosition();
        const scrollEl = scrollContainerRef?.current;

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        window.addEventListener("resize", handleReposition);
        scrollEl?.addEventListener("scroll", handleReposition, { passive: true });
        window.addEventListener("scroll", handleReposition, { passive: true });

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
            window.removeEventListener("resize", handleReposition);
            scrollEl?.removeEventListener("scroll", handleReposition);
            window.removeEventListener("scroll", handleReposition);
        };
    }, [isOpen, onClose, updatePosition, scrollContainerRef]);

    return (
        <span ref={anchorRef} className="shrink-0">
            <button
                type="button"
                className="text-gray-400/60 transition-colors hover:text-gray-500"
                title="Historical container quantity"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                    event.stopPropagation();
                    if (isOpen) {
                        onClose();
                    } else {
                        onOpen();
                    }
                }}
            >
                <QuestionMarkCircleIcon className="h-3 w-3" aria-hidden />
            </button>
            {isOpen
                ? createPortal(
                      <div
                          ref={popoverRef}
                          className="fixed z-[300] w-64 rounded-md border border-gray-200 bg-white p-3 shadow-xl"
                          style={{ top: pos.top, left: pos.left }}
                          onMouseDown={(event) => event.stopPropagation()}
                      >
                          <div className="space-y-2">
                              <p className="text-xs font-semibold text-gray-900">Historical Container Quantity</p>
                              <div
                                  className={`flex items-center gap-2 rounded px-2.5 py-1.5 text-xs font-medium ${
                                      isContainer
                                          ? "bg-blue-50 text-blue-700"
                                          : "bg-amber-50 text-amber-700"
                                  }`}
                              >
                                  {isContainer ? (
                                      <CubeIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                  ) : (
                                      <ArchiveBoxIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                  )}
                                  <span>Ships by {method}</span>
                              </div>
                              <p className="text-[11px] leading-relaxed text-gray-500">{note}</p>
                              <p className="mt-1 border-t pt-2 text-[10px] text-gray-400/80">
                                  Source: Inbound Shipments · Connect via Eadin API
                              </p>
                          </div>
                      </div>,
                      document.body
                  )
                : null}
        </span>
    );
}

function wrapContainerQtyCellContent(content, row, columnId, containerQtyHelpContext, rowKey) {
    if (columnId !== "container_qty") return content;
    const { openProductId, setOpenProductId, scrollContainerRef } = containerQtyHelpContext;
    const isOpen = openProductId != null && openProductId === rowKey;
    return (
        <div className="flex w-full min-w-0 items-center gap-1">
            <div className="min-w-0 flex-1">{content}</div>
            <ContainerQtyHistoricalHelp
                row={row}
                isOpen={isOpen}
                onOpen={() => setOpenProductId(rowKey)}
                onClose={() => setOpenProductId(null)}
                scrollContainerRef={scrollContainerRef}
            />
        </div>
    );
}

/** Width of the leading bulk-select checkbox column (sticky) — matches catalog grid w-10 */
const CHECKBOX_COL_WIDTH = 40;

/**
 * Single source of truth for column width (px). Sticky `left`, `<th>`, and `<td>` must all match
 * or pinned columns drift over neighbors when scrolling (`table-layout: auto` expands cells).
 */
function getColumnCellWidthPx(column, fillableSet) {
    const raw = column?.width;
    if (raw != null && raw !== "" && !Number.isNaN(Number(raw))) {
        const n = Math.max(0, Number(raw));
        // Under `table-layout: fixed`, 40px + px-3 padding cannot fit the avatar; min matches real layout.
        if (column?.id === "image") return Math.max(56, n);
        return n;
    }
    if (column?.id === "image") return 88;
    if (fillableSet?.has(column.id)) return 120;
    return 160;
}

/** Subtle row avatar tints (aligned with catalog/CatalogTable.tsx) */
const ROW_TINTS = ["#f1f5f9", "#f5f5f4", "#f4f4f5", "#f5f5f5", "#f3f4f6"];

const getPinnedStyles = (column, pinnedOffsets) => {
    if (column.pinned !== "left") return {};
    return {
        position: "sticky",
        left: `${pinnedOffsets[column.id] || 0}px`,
        zIndex: 10,
    };
};

const renderDefaultValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return EMPTY_VALUE;
    }
    return value;
};

const formatCatalogMsrpDisplay = (raw) => {
    if (raw === null || raw === undefined || raw === "") {
        return EMPTY_VALUE;
    }
    const normalized = String(raw).trim();
    if (!normalized || normalized === EMPTY_VALUE) {
        return EMPTY_VALUE;
    }
    const n = parseFloat(normalized.replace(/[^0-9.-]/g, ""));
    if (!Number.isFinite(n)) {
        return EMPTY_VALUE;
    }
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

const normalizeManualMsrpInput = (raw) => {
    const normalized = String(raw ?? "").trim();
    if (!normalized || normalized === EMPTY_VALUE) {
        return null;
    }
    const n = parseFloat(normalized.replace(/[^0-9.-]/g, ""));
    if (!Number.isFinite(n) || n <= 0) {
        return null;
    }
    return String(Number(n.toFixed(2)));
};

const formatPlannedPriceDate = (raw) => {
    const match = String(raw || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? `${match[2]}/${match[3]}/${match[1]}` : "n/a";
};

/** Lifecycle date columns: editable in-grid via DatePickerInput; fallback display as MM/DD/YYYY. */
const CATALOG_TABLE_DATE_DISPLAY_COLUMNS = new Set(["launch_date", "sunset_date", "inactive_date"]);

function formatCatalogTableDateDisplay(value) {
    if (value === null || value === undefined || value === "") {
        return EMPTY_VALUE;
    }
    const s = String(value).trim();
    if (s === "" || s === EMPTY_VALUE) {
        return EMPTY_VALUE;
    }
    const isoDay = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (isoDay) {
        const [, y, mo, d] = isoDay;
        return `${mo}/${d}/${y}`;
    }
    const t = Date.parse(s);
    if (!Number.isNaN(t)) {
        return new Date(t).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        });
    }
    return s;
}

/** Image placeholder: first two letters of canonical name only (catalog-style initials) */
function getCanonicalNameInitials(item) {
    const v = item?.canonical_product_name;
    if (v == null || v === undefined) return "?";
    const s = String(v).trim();
    if (s === "" || s === EMPTY_VALUE) return "?";
    return s.slice(0, 2).toUpperCase();
}
const isTruthyCellValue = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        return ["true", "1", "yes"].includes(normalized);
    }
    return false;
};
const renderBooleanMark = (value) => {
    const mark = isTruthyCellValue(value) ? "✓" : "·";
    return (
        <span
            className={`inline-flex items-center justify-center w-5 h-5 rounded text-xs font-bold ${
                isTruthyCellValue(value) ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"
            }`}
        >
            {mark}
        </span>
    );
};

function renderSkuValues(value) {
    const display = renderDefaultValue(value);
    if (display === EMPTY_VALUE) {
        return <SupplySku sku={EMPTY_VALUE} />;
    }

    const skus = String(display)
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

    if (skus.length === 0) {
        return <SupplySku sku={EMPTY_VALUE} />;
    }

    return (
        <div className="flex flex-wrap gap-1">
            {skus.map((sku, index) => (
                <SupplySku key={`${sku}-${index}`} sku={sku} />
            ))}
        </div>
    );
}

export const ProductCatalogTable = forwardRef(({
    items,
    columns,
    isLoading,
    onEditItem,
    categorySettingsItems = [],
    presetSettingsByType = {},
    containerSettingsItems = [],
    vendorSelectOptions = [],
    vendorSelectLoading = false,
    /** Register a cell edit for deferred save (no API until parent saves). */
    onCellDraftChange,
    onBulkUpdateSelected,
    onBulkSectionApply,
    /** `deleted_products` shows bulk restore; other views show bulk delete. */
    catalogView = "default",
    onBulkDeleteSelected,
    onBulkRestoreSelected,
    isBulkDeleteOrRestoreBusy = false,
    isBulkUpdating = false,
    selectionResetKey = 0,
    sortBy = "",
    sortOrder = "asc",
    onSortColumn,
    /** Extra classes for the scroll wrapper (e.g. layout); scrollbar skin is `catalog-table-scroll` */
    scrollContainerClassName = "",
    /**
     * Measured height (px) of the Product Catalog toolbar block; scroll max-height = `calc(100vh - value)`.
     */
    toolbarBlockHeightPx = null,
}, ref) => {
    const [tableItems, setTableItems] = useState(items || []);
    const [activeCell, setActiveCell] = useState(null);
    const [editingCell, setEditingCell] = useState(null);
    const [isDragFilling, setIsDragFilling] = useState(false);
    const [dragFillRow, setDragFillRow] = useState(null);
    const [inlineEdit, setInlineEdit] = useState(null);
    const inlineEditRef = useRef(null);
    const tableItemsRef = useRef(items || []);
    const commitInlineEditRef = useRef(() => Promise.resolve());
    const tableScrollRef = useRef(null);
    const dropdownAnchorRef = useRef(null);
    const headerCheckboxRef = useRef(null);
    const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
    const [bulkCategoryContextId, setBulkCategoryContextId] = useState(null);
    const [bulkSelectNonce, setBulkSelectNonce] = useState(0);
    const [bulkEditConfirmOpen, setBulkEditConfirmOpen] = useState(false);
    const [pendingBulkEdit, setPendingBulkEdit] = useState(null);
    const [bulkSectionModal, setBulkSectionModal] = useState(null);
    const [openBulkLifecycleAction, setOpenBulkLifecycleAction] = useState(null);
    const bulkLifecycleToolbarRef = useRef(null);
    const [openContainerQtyHelpProductId, setOpenContainerQtyHelpProductId] = useState(null);

    const containerQtyHelpContext = useMemo(
        () => ({
            openProductId: openContainerQtyHelpProductId,
            setOpenProductId: setOpenContainerQtyHelpProductId,
            scrollContainerRef: tableScrollRef,
        }),
        [openContainerQtyHelpProductId]
    );

    const onCellDraftChangeRef = useRef(onCellDraftChange);
    useEffect(() => {
        onCellDraftChangeRef.current = onCellDraftChange;
    }, [onCellDraftChange]);

    useImperativeHandle(ref, () => ({
        scrollToTop: () => {
            tableScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        },
    }));
    const [dropdownMenuPos, setDropdownMenuPos] = useState(null);

    useEffect(() => {
        setTableItems(items || []);
    }, [items]);

    useEffect(() => {
        const visibleIds = new Set((items || []).map((item) => item?.id).filter((id) => id != null));
        setSelectedRowIds((prev) => {
            if (!prev || prev.size === 0) return prev;
            const next = new Set([...prev].filter((id) => visibleIds.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [items]);

    useEffect(() => {
        setSelectedRowIds(new Set());
        setBulkCategoryContextId(null);
        setBulkEditConfirmOpen(false);
        setPendingBulkEdit(null);
        setBulkSectionModal(null);
        setOpenBulkLifecycleAction(null);
    }, [selectionResetKey]);

    const selectedIdList = useMemo(() => [...selectedRowIds], [selectedRowIds]);
    const selectedCount = selectedRowIds.size;
    const isDeletedProductsView = catalogView === "deleted_products";

    const commonCategoryGroupIdFromSelection = useMemo(() => {
        if (selectedRowIds.size === 0) return null;
        const rows = (tableItems || []).filter((r) => selectedRowIds.has(r.id));
        const ids = [...new Set(rows.map((r) => r.category_group_id).filter((x) => x != null && x !== ""))];
        if (ids.length !== 1) return null;
        return Number(ids[0]);
    }, [tableItems, selectedRowIds]);

    const subcategoryBulkGroupId = bulkCategoryContextId ?? commonCategoryGroupIdFromSelection;

    const allPageIdsSelected =
        tableItems.length > 0 && tableItems.every((r) => selectedRowIds.has(r.id));
    const somePageIdsSelected = tableItems.some((r) => selectedRowIds.has(r.id));

    useEffect(() => {
        const el = headerCheckboxRef.current;
        if (!el) return;
        el.indeterminate = somePageIdsSelected && !allPageIdsSelected;
    }, [somePageIdsSelected, allPageIdsSelected]);

    const toggleRowSelected = (id) => {
        setSelectedRowIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAllOnPage = () => {
        setSelectedRowIds((prev) => {
            const next = new Set(prev);
            if (allPageIdsSelected) {
                tableItems.forEach((r) => next.delete(r.id));
            } else {
                tableItems.forEach((r) => next.add(r.id));
            }
            return next;
        });
    };

    const clearRowSelection = () => {
        setSelectedRowIds(new Set());
        setBulkCategoryContextId(null);
        setBulkEditConfirmOpen(false);
        setPendingBulkEdit(null);
        setBulkSectionModal(null);
        setOpenBulkLifecycleAction(null);
    };

    const applyBulkProductDelete = async () => {
        if (!onBulkDeleteSelected || selectedIdList.length === 0) return;
        try {
            await onBulkDeleteSelected(selectedIdList);
            clearRowSelection();
        } catch (error) {
            console.error(error);
        }
    };

    const applyBulkProductRestore = async () => {
        if (!onBulkRestoreSelected || selectedIdList.length === 0) return;
        try {
            await onBulkRestoreSelected(selectedIdList);
            clearRowSelection();
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (!openBulkLifecycleAction) return;
        const onDoc = (e) => {
            if (bulkLifecycleToolbarRef.current && !bulkLifecycleToolbarRef.current.contains(e.target)) {
                setOpenBulkLifecycleAction(null);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpenBulkLifecycleAction(null);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [openBulkLifecycleAction]);

    useEffect(() => {
        if (selectedCount === 0) setOpenBulkLifecycleAction(null);
    }, [selectedCount]);

    const runBulkUpdate = async (payload) => {
        if (!onBulkUpdateSelected || selectedIdList.length === 0) return;
        await onBulkUpdateSelected(selectedIdList, payload);
        setBulkSelectNonce((n) => n + 1);
    };

    const cancelBulkEditConfirm = () => {
        setBulkEditConfirmOpen(false);
        setPendingBulkEdit(null);
        setBulkSelectNonce((n) => n + 1);
    };

    const confirmBulkEditApply = async () => {
        if (!pendingBulkEdit || !onBulkUpdateSelected) return;
        try {
            if (pendingBulkEdit.setCategoryContextId != null) {
                setBulkCategoryContextId(pendingBulkEdit.setCategoryContextId);
            }
            await runBulkUpdate(pendingBulkEdit.payload);
            setBulkEditConfirmOpen(false);
            setPendingBulkEdit(null);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        inlineEditRef.current = inlineEdit;
    }, [inlineEdit]);

    useEffect(() => {
        tableItemsRef.current = tableItems;
    }, [tableItems]);

    const integerInputColumns = useMemo(
        () => new Set(["lead_time_days", "transit_lead_time_days", "moq", "container_qty", "carton_qty"]),
        []
    );
    const physicalInputColumns = useMemo(
        () => new Set(["unit_weight_lbs", "unit_length", "unit_width", "unit_height"]),
        []
    );
    const dateInputColumns = useMemo(
        () => new Set(["launch_date", "sunset_date", "inactive_date"]),
        []
    );
    const inputEditableColumns = useMemo(
        () =>
            new Set([
                "lead_time_days",
                "transit_lead_time_days",
                "moq",
                "container_qty",
                "carton_qty",
                "unit_weight_lbs",
                "unit_length",
                "unit_width",
                "unit_height",
                "color",
                "material",
                "shopify_planned_gross_price",
                "amazon_planned_gross_price",
            ]),
        []
    );

    const textareaEditableColumns = useMemo(() => new Set(["material"]), []);

    const fillableColumns = useMemo(
        () =>
            new Set([
                "category",
                "subcategory",
                "status",
                "entry_engine",
                "brand_pillar",
                "demand_tier",
                "intent",
                "supplier",
                "lead_time_days",
                "transit_lead_time_days",
                "moq",
                "container_group_id",
                "container_qty",
                "carton_qty",
                "unit_weight_lbs",
                "unit_length",
                "unit_width",
                "unit_height",
                "color",
                "material",
                "launch_date",
                "sunset_date",
                "inactive_date",
                "shopify_planned_gross_price",
                "amazon_planned_gross_price",
            ]),
        []
    );
    const dropdownEditableColumns = useMemo(
        () => new Set([
            "category",
            "subcategory",
            "status",
            "entry_engine",
            "brand_pillar",
            "demand_tier",
            "intent",
            "container_group_id",
            "supplier",
        ]),
        []
    );

    const statusOptions = useMemo(
        () => [
            { label: "Active", value: "ACTIVE" },
            { label: "Inactive", value: "IN_ACTIVE" },
            { label: "Sunset", value: "SUNSET" },
            { label: "Planned", value: "PLANNED" },
        ],
        []
    );
    const entryEngineOptions = useMemo(
        () => (presetSettingsByType?.entry_engine || []).map((item) => ({ label: item.name, value: String(item.id) })),
        [presetSettingsByType?.entry_engine]
    );
    const demandTierOptions = useMemo(
        () => (presetSettingsByType?.demand_tier || []).map((item) => ({ label: item.name, value: String(item.id) })),
        [presetSettingsByType?.demand_tier]
    );
    const brandPillarOptions = useMemo(
        () => (presetSettingsByType?.brand_pillar || []).map((item) => ({ label: item.name, value: String(item.id) })),
        [presetSettingsByType?.brand_pillar]
    );
    const intentOptions = useMemo(
        () => (presetSettingsByType?.intent || []).map((item) => ({ label: item.name, value: String(item.id) })),
        [presetSettingsByType?.intent]
    );
    const containerGroupOptions = useMemo(
        () => (containerSettingsItems || []).map((item) => ({ label: item.name, value: String(item.id) })),
        [containerSettingsItems]
    );
    const containerGroupLabelById = useMemo(
        () => new Map(containerGroupOptions.map((opt) => [opt.value, opt.label])),
        [containerGroupOptions]
    );
    const getContainerGroupLabel = useCallback(
        (id, row) => {
            if (id != null && id !== "") {
                const fromOptions = containerGroupLabelById.get(String(id));
                if (fromOptions) return fromOptions;
            }
            const legacy = row?.container_group;
            if (legacy != null && legacy !== "" && legacy !== "-") {
                return legacy;
            }
            return EMPTY_VALUE;
        },
        [containerGroupLabelById]
    );
    const categoryOptions = useMemo(
        () => (categorySettingsItems || []).map((group) => ({
            label: group.name,
            value: String(group.id),
            groupId: group.id,
        })),
        [categorySettingsItems]
    );

    const subcategoryOptionsForBulkGroup = useMemo(() => {
        if (!subcategoryBulkGroupId) return [];
        const group = (categorySettingsItems || []).find(
            (item) => Number(item.id) === Number(subcategoryBulkGroupId)
        );
        return (group?.variants || []).map((variant) => ({
            label: variant.name,
            value: String(variant.id),
        }));
    }, [categorySettingsItems, subcategoryBulkGroupId]);

    /** Selected rows reference more than one category — subcategory bulk needs one category (use Set category first). */
    const selectionHasMultipleCategories = useMemo(() => {
        if (selectedRowIds.size === 0) return false;
        const rows = (tableItems || []).filter((r) => selectedRowIds.has(r.id));
        const distinct = [
            ...new Set(rows.map((r) => r.category_group_id).filter((x) => x != null && x !== "")),
        ];
        return distinct.length > 1;
    }, [tableItems, selectedRowIds]);

    const bulkSubcategoryDisabled =
        isBulkUpdating ||
        !onBulkUpdateSelected ||
        !subcategoryBulkGroupId ||
        subcategoryOptionsForBulkGroup.length === 0;

    /** Native `title` on bulk Subcategory menu trigger when disabled / mixed categories. */
    const bulkSubcategoryTooltip = useMemo(() => {
        if (isBulkUpdating) return "Updating…";
        if (!onBulkUpdateSelected) return "Unavailable";
        if (!subcategoryBulkGroupId) {
            if (selectionHasMultipleCategories) {
                return "Different categories — set category first";
            }
            return "Select a category first";
        }
        if (subcategoryOptionsForBulkGroup.length === 0) return "No subcategories";
        return undefined;
    }, [
        isBulkUpdating,
        onBulkUpdateSelected,
        subcategoryBulkGroupId,
        subcategoryOptionsForBulkGroup.length,
        selectionHasMultipleCategories,
    ]);

    const vendorOptions = useMemo(
        () =>
            (vendorSelectOptions || []).map((opt) => ({
                label: opt.label,
                value: String(opt.value),
            })),
        [vendorSelectOptions]
    );

    const getSubcategoryOptions = (row) => {
        const groupId = Number(row?.category_group_id || 0);
        if (!groupId) return [];
        const group = (categorySettingsItems || []).find((item) => Number(item.id) === groupId);
        return (group?.variants || []).map((variant) => ({
            label: variant.name,
            value: String(variant.id),
            variantId: variant.id,
            groupId: variant.group_id,
        }));
    };

    const getSelectValueForColumn = useCallback((row, columnId) => {
        if (columnId === "category") {
            return row?.category_group_id ? String(row.category_group_id) : "";
        }
        if (columnId === "subcategory") {
            return row?.subcategory_variant_id ? String(row.subcategory_variant_id) : "";
        }
        if (columnId === "entry_engine") {
            return row?.entry_engine_id ? String(row.entry_engine_id) : "";
        }
        if (columnId === "brand_pillar") {
            return row?.brand_pillar_id ? String(row.brand_pillar_id) : "";
        }
        if (columnId === "demand_tier") {
            return row?.demand_tier_id ? String(row.demand_tier_id) : "";
        }
        if (columnId === "intent") {
            return row?.intent_id ? String(row.intent_id) : "";
        }
        if (columnId === "container_group_id") {
            return row?.container_group_id != null && row?.container_group_id !== ""
                ? String(row.container_group_id)
                : "";
        }
        if (columnId === "supplier") {
            return row?.vendor_id !== undefined &&
                row?.vendor_id !== null &&
                row?.vendor_id !== ""
                ? String(row.vendor_id)
                : "";
        }
        return row?.[columnId] || "";
    }, []);

    const getOptionsByColumn = useCallback(
        (columnId, row) => {
            if (columnId === "status") return statusOptions;
            if (columnId === "entry_engine") return entryEngineOptions;
            if (columnId === "brand_pillar") return brandPillarOptions;
            if (columnId === "demand_tier") return demandTierOptions;
            if (columnId === "intent") return intentOptions;
            if (columnId === "container_group_id") return containerGroupOptions;
            if (columnId === "category") return categoryOptions;
            if (columnId === "subcategory") return getSubcategoryOptions(row);
            if (columnId === "supplier") return vendorOptions;
            return [];
        },
        [
            statusOptions,
            entryEngineOptions,
            brandPillarOptions,
            demandTierOptions,
            intentOptions,
            containerGroupOptions,
            categoryOptions,
            categorySettingsItems,
            vendorOptions,
        ]
    );

    const getPresetMetaByColumn = (columnId) => {
        if (columnId === "entry_engine") return { idField: "entry_engine_id", displayField: "entry_engine", options: entryEngineOptions };
        if (columnId === "brand_pillar") return { idField: "brand_pillar_id", displayField: "brand_pillar", options: brandPillarOptions };
        if (columnId === "demand_tier") return { idField: "demand_tier_id", displayField: "demand_tier", options: demandTierOptions };
        if (columnId === "intent") return { idField: "intent_id", displayField: "intent", options: intentOptions };
        return null;
    };

    const applyCellValue = (row, columnId, nextValue) => {
        if (columnId === "category") {
            const selected = categoryOptions.find((opt) => opt.value === nextValue);
            return {
                ...row,
                category: selected?.label || EMPTY_VALUE,
                category_group_id: selected?.groupId || null,
                subcategory: EMPTY_VALUE,
                subcategory_variant_id: null,
            };
        }
        if (columnId === "subcategory") {
            const selected = getSubcategoryOptions(row).find((opt) => opt.value === nextValue);
            return {
                ...row,
                subcategory: selected?.label || EMPTY_VALUE,
                subcategory_variant_id: selected?.variantId || null,
                category_group_id: selected?.groupId || row?.category_group_id || null,
            };
        }
        if (columnId === "supplier") {
            const selected = vendorOptions.find((opt) => String(opt.value) === String(nextValue));
            return {
                ...row,
                supplier: selected?.label || EMPTY_VALUE,
                vendor_id: nextValue ? nextValue : null,
            };
        }
        if (columnId === "container_group_id") {
            const selected = containerGroupOptions.find((opt) => opt.value === nextValue);
            return {
                ...row,
                container_group_id: selected ? Number(selected.value) : null,
                container_group: selected?.label || EMPTY_VALUE,
            };
        }
        if (integerInputColumns.has(columnId)) {
            const t = sanitizeUnsignedIntegerString(String(nextValue ?? "").trim());
            if (t === "") {
                const cleared = { ...row, [columnId]: EMPTY_VALUE };
                if (columnId === "lead_time_days" || columnId === "transit_lead_time_days") {
                    return { ...cleared, total_lead_time_days: recomputeTotalLeadTimeDays(cleared) };
                }
                return cleared;
            }
            const n = parseInt(t, 10);
            if (!Number.isFinite(n)) {
                return row;
            }
            const updated = { ...row, [columnId]: n };
            if (columnId === "lead_time_days" || columnId === "transit_lead_time_days") {
                return { ...updated, total_lead_time_days: recomputeTotalLeadTimeDays(updated) };
            }
            return updated;
        }
        if (columnId === "color" || columnId === "material") {
            const t = String(nextValue ?? "").trim();
            return { ...row, [columnId]: t === "" ? EMPTY_VALUE : t };
        }
        if (columnId === "msrp") {
            const manualMsrp = normalizeManualMsrpInput(nextValue);
            return {
                ...row,
                manual_msrp: manualMsrp,
                msrp: manualMsrp ? formatCatalogMsrpDisplay(manualMsrp) : EMPTY_VALUE,
            };
        }
        if (columnId === "shopify_planned_gross_price" || columnId === "amazon_planned_gross_price") {
            const value = normalizeManualMsrpInput(nextValue);
            return { ...row, [columnId]: value == null ? null : Number(value) };
        }
        if (physicalInputColumns.has(columnId)) {
            const t = String(nextValue ?? "").trim();
            return { ...row, [columnId]: t === "" ? EMPTY_VALUE : t };
        }
        if (dateInputColumns.has(columnId)) {
            const t = String(nextValue ?? "").trim();
            if (t === "") {
                return { ...row, [columnId]: EMPTY_VALUE };
            }
            return /^\d{4}-\d{2}-\d{2}$/.test(t) ? { ...row, [columnId]: t } : row;
        }
        const presetMeta = getPresetMetaByColumn(columnId);
        if (presetMeta) {
            const selected = presetMeta.options.find((opt) => opt.value === nextValue);
            return {
                ...row,
                [presetMeta.displayField]: selected?.label || EMPTY_VALUE,
                [presetMeta.idField]: selected ? Number(selected.value) : null,
            };
        }
        return {
            ...row,
            [columnId]: nextValue,
        };
    };

    const buildPersistPayloadFromRow = (row, columnId) => {
        const payload = {};
        if (columnId === "category") {
            payload.category_group_id = row.category_group_id;
            payload.subcategory_variant_id = null;
        } else if (columnId === "subcategory") {
            payload.category_group_id = row.category_group_id;
            payload.subcategory_variant_id = row.subcategory_variant_id;
        } else if (columnId === "unit_weight_lbs") {
            payload.unit_weight_lbs = row.unit_weight_lbs === EMPTY_VALUE ? "" : row.unit_weight_lbs;
        } else if (columnId === "unit_length") {
            payload.unit_length = row.unit_length === EMPTY_VALUE ? "" : row.unit_length;
        } else if (columnId === "unit_width") {
            payload.unit_width = row.unit_width === EMPTY_VALUE ? "" : row.unit_width;
        } else if (columnId === "unit_height") {
            payload.unit_height = row.unit_height === EMPTY_VALUE ? "" : row.unit_height;
        } else if (columnId === "entry_engine") {
            payload.entry_engine_id = row.entry_engine_id;
        } else if (columnId === "brand_pillar") {
            payload.brand_pillar_id = row.brand_pillar_id;
        } else if (columnId === "demand_tier") {
            payload.demand_tier_id = row.demand_tier_id;
        } else if (columnId === "intent") {
            payload.intent_id = row.intent_id;
        } else if (columnId === "container_group_id") {
            payload.container_group_id = row.container_group_id;
        } else if (columnId === "supplier") {
            payload.newVendor = row.vendor_id ?? null;
        } else if (columnId === "lead_time_days") {
            payload.lead_time_days = row.lead_time_days === EMPTY_VALUE ? "" : row.lead_time_days;
        } else if (columnId === "transit_lead_time_days") {
            payload.transit_lead_time_days =
                row.transit_lead_time_days === EMPTY_VALUE ? "" : row.transit_lead_time_days;
        } else if (columnId === "moq") {
            payload.moq = row.moq === EMPTY_VALUE ? "" : row.moq;
        } else if (columnId === "color") {
            payload.color = row.color === EMPTY_VALUE ? "" : row.color;
        } else if (columnId === "material") {
            payload.material = row.material === EMPTY_VALUE ? "" : row.material;
        } else if (columnId === "container_qty") {
            payload.container_qty = row.container_qty === EMPTY_VALUE ? "" : row.container_qty;
        } else if (columnId === "carton_qty") {
            payload.carton_qty = row.carton_qty === EMPTY_VALUE ? "" : row.carton_qty;
        } else if (columnId === "launch_date") {
            payload.launch_date = row.launch_date === EMPTY_VALUE ? "" : row.launch_date;
        } else if (columnId === "sunset_date") {
            payload.sunset_date = row.sunset_date === EMPTY_VALUE ? "" : row.sunset_date;
        } else if (columnId === "inactive_date") {
            payload.inactive_date = row.inactive_date === EMPTY_VALUE ? "" : row.inactive_date;
        } else if (columnId === "msrp") {
            payload.manual_msrp = row.manual_msrp ?? "";
        } else if (columnId === "shopify_planned_gross_price" || columnId === "amazon_planned_gross_price") {
            payload[columnId] = row[columnId] == null || row[columnId] === EMPTY_VALUE ? "" : row[columnId];
            payload.planned_gross_price_note = row[`${columnId}_meta`]?.note || "";
        } else {
            payload[columnId] = row[columnId];
        }
        return payload;
    };

    const isPersistPayloadUnchanged = (row, updatedRow, columnId) =>
        JSON.stringify(buildPersistPayloadFromRow(row, columnId)) ===
        JSON.stringify(buildPersistPayloadFromRow(updatedRow, columnId));

    const notifyCellDraftChange = (row, columnId, updatedRow) => {
        const fn = onCellDraftChangeRef.current;
        if (typeof fn !== "function") return;
        const payload = buildPersistPayloadFromRow(updatedRow, columnId);
        fn(row, payload);
    };

    const commitInlineEdit = () => {
        const edit = inlineEditRef.current;
        if (!edit) return;
        const { rowIndex, columnId, value } = edit;
        const row = tableItemsRef.current[rowIndex];
        if (!row) {
            return;
        }
        let nextValue = value;
        if (integerInputColumns.has(columnId)) {
            const t = sanitizeUnsignedIntegerString(String(value ?? "").trim());
            if (t === "") {
                nextValue = "";
            } else {
                const n = parseInt(t, 10);
                if (!Number.isFinite(n)) {
                    return;
                }
                nextValue = String(n);
            }
        } else if (columnId === "color" || columnId === "material") {
            nextValue = String(value ?? "").trim();
        } else if (columnId === "msrp" || columnId === "shopify_planned_gross_price" || columnId === "amazon_planned_gross_price") {
            nextValue = String(value ?? "").trim();
        } else if (physicalInputColumns.has(columnId)) {
            nextValue = String(value ?? "").trim();
        } else if (dateInputColumns.has(columnId)) {
            nextValue = String(value ?? "").trim();
        }
        const updatedRow = applyCellValue(row, columnId, nextValue);
        inlineEditRef.current = null;
        setInlineEdit(null);
        setEditingCell(null);
        if (isPersistPayloadUnchanged(row, updatedRow, columnId)) {
            return;
        }
        setTableItems((prev) => prev.map((r, idx) => (idx === rowIndex ? updatedRow : r)));
        notifyCellDraftChange(row, columnId, updatedRow);
    };

    commitInlineEditRef.current = commitInlineEdit;

    const onCellSelectValue = (rowIndex, columnId, nextValue) => {
        const currentRow = tableItems[rowIndex];
        const updatedRow = applyCellValue(currentRow, columnId, nextValue);
        if (isPersistPayloadUnchanged(currentRow, updatedRow, columnId)) {
            return;
        }
        setTableItems((prev) => prev.map((row, idx) => (idx === rowIndex ? updatedRow : row)));
        notifyCellDraftChange(currentRow, columnId, updatedRow);
    };

    const startFill = (rowIndex, columnId) => {
        if (!fillableColumns.has(columnId)) return;
        setActiveCell({ rowIndex, columnId });
        setIsDragFilling(true);
        setDragFillRow(rowIndex);
    };

    useEffect(() => {
        const handleMouseUp = () => {
            if (!isDragFilling || !activeCell || dragFillRow === null) return;
            const { rowIndex: startRow, columnId } = activeCell;
            const endRow = dragFillRow;
            if (endRow === startRow) {
                setIsDragFilling(false);
                setDragFillRow(null);
                return;
            }
            const sourceRow = tableItems[startRow];
            const presetMetaForFill = getPresetMetaByColumn(columnId);
            const sourceValue = sourceRow?.[columnId];
            const nextTableItems = [...tableItems];
            const rangeStart = Math.min(startRow, endRow);
            const rangeEnd = Math.max(startRow, endRow);
            const operations = [];

            for (let idx = rangeStart; idx <= rangeEnd; idx += 1) {
                if (idx === startRow) continue;
                const row = nextTableItems[idx];
                if (!row) continue;
                let nextValue;
                if (columnId === "category") {
                    nextValue = sourceRow?.category_group_id ? String(sourceRow.category_group_id) : "";
                } else if (columnId === "subcategory") {
                    nextValue = sourceRow?.subcategory_variant_id ? String(sourceRow.subcategory_variant_id) : "";
                } else if (columnId === "supplier") {
                    nextValue = sourceRow?.vendor_id != null && sourceRow?.vendor_id !== ""
                        ? String(sourceRow.vendor_id)
                        : "";
                } else if (columnId === "container_group_id") {
                    nextValue =
                        sourceRow?.container_group_id != null && sourceRow?.container_group_id !== ""
                            ? String(sourceRow.container_group_id)
                            : "";
                } else if (integerInputColumns.has(columnId) || columnId === "color" || columnId === "material") {
                    const v = sourceRow?.[columnId];
                    nextValue = v === EMPTY_VALUE || v === null || v === undefined ? "" : String(v);
                } else if (physicalInputColumns.has(columnId)) {
                    const v = sourceRow?.[columnId];
                    nextValue = v === EMPTY_VALUE || v === null || v === undefined ? "" : String(v);
                } else if (dateInputColumns.has(columnId)) {
                    const v = sourceRow?.[columnId];
                    nextValue = v === EMPTY_VALUE || v === null || v === undefined ? "" : String(v).slice(0, 10);
                } else if (presetMetaForFill) {
                    const idVal = sourceRow?.[presetMetaForFill.idField];
                    nextValue = idVal != null && idVal !== "" ? String(idVal) : "";
                } else {
                    nextValue = sourceValue;
                }
                const updatedRow = applyCellValue(row, columnId, nextValue);
                if (isPersistPayloadUnchanged(row, updatedRow, columnId)) {
                    continue;
                }
                nextTableItems[idx] = updatedRow;
                operations.push({ row, columnId, updatedRow });
            }

            setIsDragFilling(false);
            setDragFillRow(null);
            setEditingCell(null);

            if (operations.length === 0) {
                return;
            }

            setTableItems(nextTableItems);

            for (const op of operations) {
                notifyCellDraftChange(op.row, op.columnId, op.updatedRow);
            }
        };
        window.addEventListener("mouseup", handleMouseUp);
        return () => window.removeEventListener("mouseup", handleMouseUp);
    }, [activeCell, dragFillRow, isDragFilling, tableItems, columns]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (isDragFilling) return;
            if (event.target?.closest?.("[data-product-catalog-dropdown]")) return;
            void commitInlineEditRef.current();
            setEditingCell(null);
            setInlineEdit(null);
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isDragFilling]);

    const updateDropdownMenuPosition = useCallback(() => {
        const el = dropdownAnchorRef.current;
        if (!el || !editingCell) return;
        const row = tableItems[editingCell.rowIndex];
        if (!row) return;
        const cellOptions = getOptionsByColumn(editingCell.columnId, row);
        const optionRows = 1 + cellOptions.length;
        const GAP = 4;
        const MENU_MAX_H = 200;
        /** ~py-1.5 + text-sm row in the portal menu */
        const APPROX_ROW_PX = 36;
        const estimatedHeight = Math.min(MENU_MAX_H, optionRows * APPROX_ROW_PX + 8);

        const r = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const spaceBelow = vh - r.bottom - GAP;
        const spaceAbove = r.top - GAP;

        let top;
        let maxHeight = MENU_MAX_H;

        if (estimatedHeight <= spaceBelow) {
            top = r.bottom + GAP;
        } else if (estimatedHeight <= spaceAbove) {
            top = r.top - GAP - estimatedHeight;
        } else if (spaceBelow >= spaceAbove) {
            top = r.bottom + GAP;
            maxHeight = Math.max(80, spaceBelow);
        } else {
            maxHeight = Math.max(80, spaceAbove);
            top = r.top - GAP - maxHeight;
        }

        if (top < GAP) top = GAP;
        if (top + maxHeight > vh - GAP) {
            top = Math.max(GAP, vh - GAP - maxHeight);
        }

        const width = Math.max(r.width, 170);
        let left = r.left;
        if (left + width > vw - GAP) {
            left = Math.max(GAP, vw - GAP - width);
        }

        setDropdownMenuPos({
            top,
            left,
            width,
            maxHeight,
        });
    }, [editingCell, tableItems, getOptionsByColumn]);

    useLayoutEffect(() => {
        if (!editingCell || !dropdownEditableColumns.has(editingCell.columnId)) {
            setDropdownMenuPos(null);
            dropdownAnchorRef.current = null;
            return;
        }
        updateDropdownMenuPosition();
        const scrollEl = tableScrollRef.current;
        const onMove = () => updateDropdownMenuPosition();
        window.addEventListener("resize", onMove);
        scrollEl?.addEventListener("scroll", onMove, { passive: true });
        return () => {
            window.removeEventListener("resize", onMove);
            scrollEl?.removeEventListener("scroll", onMove);
        };
    }, [editingCell, updateDropdownMenuPosition, dropdownEditableColumns]);

    const normalizedColumns = useMemo(
        () => normalizeCatalogColumns(columns || []),
        [columns]
    );

    const visibleColumns = useMemo(
        () => normalizedColumns.filter((column) => column.visible),
        [normalizedColumns]
    );

    const tableWidthPx = useMemo(() => {
        let sum = CHECKBOX_COL_WIDTH;
        visibleColumns.forEach((column) => {
            sum += getColumnCellWidthPx(column, fillableColumns);
        });
        return sum;
    }, [visibleColumns, fillableColumns]);

    const pinnedOffsets = useMemo(() => {
        let currentLeft = CHECKBOX_COL_WIDTH;
        const offsets = {};
        visibleColumns.forEach((column) => {
            if (column.pinned === "left") {
                offsets[column.id] = currentLeft;
                currentLeft += getColumnCellWidthPx(column, fillableColumns);
            }
        });
        return offsets;
    }, [visibleColumns, fillableColumns]);

    const dropdownPortal =
        editingCell &&
        dropdownEditableColumns.has(editingCell.columnId) &&
        dropdownMenuPos &&
        tableItems[editingCell.rowIndex]
            ? createPortal(
                  (() => {
                      const rowIndex = editingCell.rowIndex;
                      const columnId = editingCell.columnId;
                      const row = tableItems[rowIndex];
                      const cellOptions = getOptionsByColumn(columnId, row);
                      const selectValue = getSelectValueForColumn(row, columnId);
                      const supplierBusy = columnId === "supplier" && vendorSelectLoading;
                      return (
                          <div
                              data-product-catalog-dropdown
                              className="fixed z-[300] rounded-md border border-gray-200 bg-white shadow-xl py-1 overflow-y-auto min-w-[170px]"
                              style={{
                                  top: dropdownMenuPos.top,
                                  left: dropdownMenuPos.left,
                                  width: dropdownMenuPos.width,
                                  maxHeight: dropdownMenuPos.maxHeight ?? 200,
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                          >
                              <button
                                  type="button"
                                  onMouseDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                  }}
                                  disabled={supplierBusy}
                                  onClick={async () => {
                                      await onCellSelectValue(rowIndex, columnId, "");
                                      setEditingCell(null);
                                  }}
                                  className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 disabled:opacity-50 ${selectValue === "" ? "font-semibold bg-gray-50" : ""}`}
                              >
                                  -
                              </button>
                              {cellOptions.map((option) => (
                                  <button
                                      key={option.value}
                                      type="button"
                                      onMouseDown={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                      }}
                                      disabled={supplierBusy}
                                      onClick={async () => {
                                          await onCellSelectValue(rowIndex, columnId, option.value);
                                          setEditingCell(null);
                                      }}
                                      className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 disabled:opacity-50 ${option.value === selectValue ? "font-semibold bg-gray-50" : ""}`}
                                  >
                                      {option.label}
                                  </button>
                              ))}
                          </div>
                      );
                  })(),
                  document.body
              )
            : null;

    if (isLoading && !items.length) {
        return (
            <div className="flex-1 h-full w-full rounded-lg border border-gray-200 bg-white relative">
                <div className="absolute inset-0 flex items-center justify-center h-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <LoaderSpinner width="64" height="64" border="4" colorSpinner="black" />
                </div>
            </div>
        );
    }

    if (!visibleColumns.length) {
        return (
            <div className="flex-1 h-full w-full rounded-lg border border-gray-200 bg-white relative">
                <div className="flex flex-col items-center gap-2">
                    <div className="rounded-lg bg-gray-100 p-3">
                        <ArrowTopRightOnSquareIcon className="h-7 w-7 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No visible columns selected.</p>
                </div>
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="flex-1 h-full w-full rounded-lg border border-gray-200 bg-white relative flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                    <div className="rounded-lg bg-gray-100 p-3">
                        <ArrowTopRightOnSquareIcon className="h-7 w-7 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No products match your filters</p>
                </div>
            </div>
        );
    }

    /** Sticky bulk-select column — z-index aligned with catalog/CatalogTable.tsx */
    const checkboxStickyHeader = {
        position: "sticky",
        left: 0,
        top: 0,
        zIndex: 30,
        width: CHECKBOX_COL_WIDTH,
        minWidth: CHECKBOX_COL_WIDTH,
        backgroundColor: "#f9fafb",
        boxShadow: "1px 0 0 0 #e5e7eb",
    };
    const checkboxStickyBody = (selected) => ({
        position: "sticky",
        left: 0,
        zIndex: 10,
        width: CHECKBOX_COL_WIDTH,
        minWidth: CHECKBOX_COL_WIDTH,
        boxShadow: "1px 0 0 0 #e5e7eb",
        background: selected ? "rgb(243 244 246)" : "#ffffff",
    });

    const scrollMaxHeightStyle =
        toolbarBlockHeightPx != null && Number.isFinite(Number(toolbarBlockHeightPx))
            ? { maxHeight: `calc(100vh - ${Number(toolbarBlockHeightPx) + 80 + (selectedCount > 0 ? 48 : 0)}px)` }
            : undefined;
    const useToolbarMeasuredMaxHeight = scrollMaxHeightStyle != null;

    return (
        <>
        <div className="relative overflow-hidden rounded border border-gray-200 bg-white shadow-sm">
            {selectedCount > 0 && (
                <div
                    ref={bulkLifecycleToolbarRef}
                    className="flex shrink-0 items-center gap-3 border-b border-gray-800 bg-black px-4 py-2 text-white"
                >
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                            <RectangleStackIcon className="h-5 w-5 shrink-0 text-white/60" aria-hidden />
                            <span className="text-xs font-semibold tabular-nums">
                                {selectedCount} selected
                            </span>
                        </div>
                        <span className="hidden h-4 w-px shrink-0 bg-white/20 sm:block" aria-hidden />
                        <div className="shrink-0">
                            <button
                                type="button"
                                disabled={isBulkUpdating || !onBulkSectionApply}
                                onClick={() => setBulkSectionModal("identity")}
                                className="inline-flex h-7 items-center gap-1 rounded-md px-2.5 text-xs text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <span>Identity</span>
                                <ChevronDownIcon className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
                            </button>
                        </div>
                        <div className="shrink-0">
                            <ProductCatalogBulkFieldMenu
                                key={`bulk-cat-${bulkSelectNonce}`}
                                triggerLabel="Category"
                                menuTitle="Set Category"
                                options={categoryOptions.map(({ label, value }) => ({ label, value }))}
                                disabled={isBulkUpdating || !onBulkUpdateSelected}
                                onSelect={(v) => {
                                    if (!v) return;
                                    const opt = categoryOptions.find((o) => o.value === v);
                                    const label = opt?.label ?? v;
                                    setPendingBulkEdit({
                                        title: `Apply category "${label}" to ${selectedCount} product${selectedCount === 1 ? "" : "s"}? Subcategories on those products will be cleared.`,
                                        payload: { category_group_id: v, subcategory_variant_id: null },
                                        setCategoryContextId: Number(v),
                                    });
                                    setBulkEditConfirmOpen(true);
                                }}
                            />
                        </div>
                        <div className="shrink-0">
                            <ProductCatalogBulkFieldMenu
                                key={`bulk-sub-${bulkSelectNonce}-${subcategoryBulkGroupId ?? "x"}`}
                                triggerLabel="Subcategory"
                                menuTitle="Set Subcategory"
                                options={subcategoryOptionsForBulkGroup}
                                tooltip={bulkSubcategoryTooltip}
                                disabled={bulkSubcategoryDisabled}
                                menuWidthClassName="w-48"
                                menuItemsClassName="max-h-64 overflow-y-auto"
                                onSelect={(v) => {
                                    if (!v || !subcategoryBulkGroupId) return;
                                    const opt = subcategoryOptionsForBulkGroup.find((o) => o.value === v);
                                    const label = opt?.label ?? v;
                                    setPendingBulkEdit({
                                        title: `Apply subcategory "${label}" to ${selectedCount} product${selectedCount === 1 ? "" : "s"}?`,
                                        payload: {
                                            category_group_id: String(subcategoryBulkGroupId),
                                            subcategory_variant_id: v,
                                        },
                                    });
                                    setBulkEditConfirmOpen(true);
                                }}
                            />
                        </div>
                        <span className="hidden h-4 w-px shrink-0 bg-white/20 sm:block" aria-hidden />
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium uppercase tracking-wide text-white/90 sm:text-sm sm:gap-x-3">
                            {[
                                { id: "attributes", label: "Attributes" },
                                { id: "operations", label: "Operations" },
                                { id: "physical", label: "Physical" },
                                { id: "lifecycle", label: "Lifecycle" },
                                { id: "flags", label: "Flags" },
                            ].map((item, i) => (
                                <span key={item.id} className="inline-flex items-center gap-x-2 sm:gap-x-3">
                                    <button
                                        type="button"
                                        className="transition-colors duration-200 h-7 gap-1 text-xs text-white/80 hover:text-white px-2.5 disabled:cursor-not-allowed disabled:opacity-50"
                                        disabled={isBulkUpdating || !onBulkSectionApply}
                                        onClick={() => setBulkSectionModal(item.id)}
                                    >
                                        {item.label}
                                    </button>
                                </span>
                            ))}
                        </div>
                        <span className="hidden h-4 w-px shrink-0 bg-white/20 sm:block" aria-hidden />
                        {!isDeletedProductsView && onBulkDeleteSelected ? (
                            <div className="relative shrink-0">
                                <CatalogBulkActionButton
                                    Icon={TrashIcon}
                                    label="Delete"
                                    isOpen={openBulkLifecycleAction === "delete"}
                                    onClick={() =>
                                        setOpenBulkLifecycleAction((prev) =>
                                            prev === "delete" ? null : "delete",
                                        )
                                    }
                                    disabled={isBulkUpdating || isBulkDeleteOrRestoreBusy}
                                    danger
                                />
                                {openBulkLifecycleAction === "delete" ? (
                                    <CatalogBulkDeleteConfirm
                                        count={selectedCount}
                                        busy={isBulkDeleteOrRestoreBusy}
                                        onCancel={() => setOpenBulkLifecycleAction(null)}
                                        onDelete={applyBulkProductDelete}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                        {isDeletedProductsView && onBulkRestoreSelected ? (
                            <div className="relative shrink-0">
                                <button
                                    type="button"
                                    className="group inline-flex h-8 items-center gap-2 rounded-md border border-white/20 bg-black px-3 text-xs font-medium text-white transition-colors hover:bg-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-50"
                                    title="Restore selected products"
                                    disabled={isBulkUpdating || isBulkDeleteOrRestoreBusy}
                                    onClick={() =>
                                        setOpenBulkLifecycleAction((prev) =>
                                            prev === "restore" ? null : "restore",
                                        )
                                    }
                                >
                                    <span>Restore</span>
                                    {isBulkDeleteOrRestoreBusy ? (
                                        <ArrowPathIcon
                                            className="h-4 w-4 shrink-0 animate-spin text-white"
                                            aria-hidden
                                        />
                                    ) : (
                                        <ArrowUturnLeftIcon className="h-4 w-4 shrink-0 text-white" aria-hidden />
                                    )}
                                    {openBulkLifecycleAction === "restore" ? (
                                        <ChevronUpIcon className="h-3.5 w-3.5 shrink-0 text-white" aria-hidden />
                                    ) : (
                                        <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-white/80" aria-hidden />
                                    )}
                                </button>
                                {openBulkLifecycleAction === "restore" ? (
                                    <CatalogBulkRestoreConfirm
                                        count={selectedCount}
                                        busy={isBulkDeleteOrRestoreBusy}
                                        onCancel={() => setOpenBulkLifecycleAction(null)}
                                        onRestore={applyBulkProductRestore}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={clearRowSelection}
                        className="shrink-0 rounded-md p-1.5 text-white/80 transition hover:bg-white/10 hover:text-white"
                        aria-label="Clear selection"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
            )}
            <div
                ref={tableScrollRef}
                className={`catalog-table-scroll hidden overflow-auto select-none sm:block ${useToolbarMeasuredMaxHeight ? "" : "max-h-[calc(100vh-220px)]"} ${scrollContainerClassName}`.trim()}
                style={scrollMaxHeightStyle}
            >
                <table
                    className="border-collapse text-sm tabular-nums"
                    style={{
                        width: tableWidthPx,
                        minWidth: tableWidthPx,
                        tableLayout: "fixed",
                    }}
                >
                    <thead className="sticky top-0 z-30">
                        <tr style={{ boxShadow: "0 1px 0 0 #e5e7eb" }}>
                            <th
                                scope="col"
                                className={`${TD_CELL_PAD} text-left`}
                                style={checkboxStickyHeader}
                            >
                                <input
                                    ref={headerCheckboxRef}
                                    type="checkbox"
                                    className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-primary focus:ring-primary focus:ring-offset-0"
                                    checked={allPageIdsSelected}
                                    onChange={toggleSelectAllOnPage}
                                    aria-label="Select all rows on this page"
                                />
                            </th>
                            {visibleColumns.map((column) => {
                                const wPx = getColumnCellWidthPx(column, fillableColumns);
                                const isSorted = sortBy === column.id;
                                const sortEnabled =
                                    typeof onSortColumn === "function" && column.sortable === true;
                                const showReadonlyLock = column.readonly === true;
                                const hideColumnTitle =
                                    column.id === "image" || String(column.label ?? "").trim() === "";
                                const headerLabel = (
                                    <span className="flex w-full min-w-0 items-center gap-1 text-left">
                                        {!hideColumnTitle ? (
                                            <span className="min-w-0 flex-1 truncate">{column.label}</span>
                                        ) : null}
                                        {column.id === "is_protected" ? (
                                            <CatalogInfoTip text={CATALOG_PROTECTED_INFO} />
                                        ) : null}
                                        {column.id === "max_discount_pct" ? (
                                            <CatalogInfoTip text={CATALOG_MAX_DISCOUNT_PCT_INFO} />
                                        ) : null}
                                        {column.id === "max_discount_price" ? (
                                            <CatalogInfoTip text={CATALOG_MAX_DISCOUNT_PRICE_INFO} />
                                        ) : null}
                                        {column.id === "msrp" ? <CatalogInfoTip text={CATALOG_MSRP_INFO} /> : null}
                                        {column.id === "shopify_planned_gross_price" ? (
                                            <CatalogInfoTip text={CATALOG_SHOPIFY_PLANNED_GROSS_PRICE_INFO} />
                                        ) : null}
                                        {column.id === "amazon_planned_gross_price" ? (
                                            <CatalogInfoTip text={CATALOG_AMAZON_PLANNED_GROSS_PRICE_INFO} />
                                        ) : null}
                                        {showReadonlyLock ? (
                                            <span
                                                className="inline-flex shrink-0 text-amber-500"
                                                title="Read-only column"
                                            >
                                                <LockClosedIcon className="h-3 w-3" aria-hidden />
                                                <span className="sr-only">Read-only column</span>
                                            </span>
                                        ) : null}
                                       {sortEnabled ? (
                                            isSorted && (
                                                sortOrder === "asc" ? (
                                                    <ArrowUpIcon className="h-[13px] w-[13px] shrink-0 text-gray-900" aria-hidden />
                                                ) : (
                                                    <ArrowDownIcon className="h-[13px] w-[13px] shrink-0 text-gray-900 " aria-hidden />
                                                )
                                            )
                                        ) : (
                                            <div />
                                        )}
                                    </span>
                                );
                                const headerPad = column.id === "image" ? "px-2 py-2.5" : "px-3 py-2.5";
                                return (
                                    <th
                                        key={column.id}
                                        aria-sort={
                                            sortEnabled && isSorted
                                                ? sortOrder === "asc"
                                                    ? "ascending"
                                                    : "descending"
                                                : "none"
                                        }
                                        aria-label={hideColumnTitle ? "Image" : undefined}
                                        className={`${headerPad} overflow-hidden text-left text-[11px] font-semibold whitespace-nowrap uppercase tracking-widest text-gray-400 ${column.pinned === "left" ? "shadow-[1px_0_0_0_#e5e7eb]" : ""}`}
                                        style={{
                                            width: wPx,
                                            minWidth: wPx,
                                            maxWidth: wPx,
                                            boxSizing: "border-box",
                                            backgroundColor: "#f9fafb",
                                            ...getPinnedStyles(column, pinnedOffsets),
                                            zIndex: column.pinned === "left" ? 25 : 20,
                                        }}
                                        onClick={() => {
                                            if (sortEnabled) {
                                                onSortColumn(column.id);
                                            }
                                        }}
                                    >
                                        {headerLabel}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {tableItems.map((item, index) => {
                            const rowSelected = selectedRowIds.has(item.id);
                            const rowTint =
                                ROW_TINTS[(item.supply_sku?.charCodeAt(0) ?? index) % ROW_TINTS.length];
                            const rowSurface = rowSelected ? "bg-gray-100" : "bg-white";

                            return (
                                <tr
                                    key={item.id}
                                    className={`catalog-style-row transition-colors duration-75 hover:bg-gray-50/80 ${rowSelected ? "bg-gray-100" : ""}`}
                                >
                                    <td
                                        className={`border-b border-gray-100 ${TD_CELL_PAD} align-middle ${rowSurface}`}
                                        style={checkboxStickyBody(rowSelected)}
                                    >
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-primary focus:ring-primary focus:ring-offset-0"
                                            checked={selectedRowIds.has(item.id)}
                                            onChange={() => toggleRowSelected(item.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={`Select row ${item.supply_sku || item.id}`}
                                        />
                                    </td>
                                    {visibleColumns.map((column) => {
                                        const wPx = getColumnCellWidthPx(column, fillableColumns);
                                        const commonCellClass = `border-b border-gray-100 ${TD_CELL_PAD} align-middle text-sm text-gray-800 ${rowSurface} ${column.pinned === "left" ? "shadow-[1px_0_0_0_#e5e7eb]" : ""}`;

                                        if (column.id === "image") {
                                            const initials = getCanonicalNameInitials(item);
                                            const rawImg = item.image;
                                            const useProductImage =
                                                Boolean(rawImg) &&
                                                !String(rawImg).includes("placehold.co") &&
                                                String(rawImg).trim() !== "";

                                            return (
                                                <td
                                                    key={column.id}
                                                    className={`border-b border-gray-100 px-2 py-2 align-middle text-sm text-gray-800 ${rowSurface} ${column.pinned === "left" ? "shadow-[1px_0_0_0_#e5e7eb]" : ""} overflow-hidden`}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                        backgroundColor: rowSelected ? "rgb(243 244 246)" : "#ffffff",
                                                    }}
                                                >
                                                    <div
                                                        className="relative mx-auto h-9 w-9 max-w-full shrink-0 overflow-hidden rounded-md border border-gray-200/80"
                                                        style={{ background: rowTint }}
                                                    >
                                                        {useProductImage ? (
                                                            <>
                                                                <img
                                                                    src={rawImg}
                                                                    alt=""
                                                                    className="h-full w-full object-contain"
                                                                    onError={(e) => {
                                                                        e.target.style.display = "none";
                                                                        const el = e.target.nextElementSibling;
                                                                        if (el) el.classList.remove("hidden");
                                                                    }}
                                                                />
                                                                <span className="absolute inset-0 hidden flex items-center justify-center text-[10px] font-semibold text-gray-400">
                                                                    {initials}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-gray-400 select-none">
                                                                {initials}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        }

                                        if (column.id === "supply_sku") {
                                            const display = renderDefaultValue(item.supply_sku);
                                            return (
                                                <td
                                                    key={column.id}
                                                    className={`${commonCellClass} cursor-pointer overflow-hidden`}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                    }}
                                                    title={display}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onEditItem?.(item);
                                                    }}
                                                >
                                                    <SupplySku
                                                        sku={display}
                                                        className="pointer-events-none block max-w-full truncate text-left"
                                                    />
                                                </td>
                                            );
                                        }

                                        if (column.id === "fba_asin" || column.id === "fbm_asin") {
                                            const display = renderDefaultValue(item[column.id]);
                                            return (
                                                <td
                                                    key={column.id}
                                                    className={`${commonCellClass} overflow-hidden`}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                    }}
                                                    title={display}
                                                >
                                                    <span
                                                        className={`pointer-events-none block w-full truncate text-left text-sm ${
                                                            display === EMPTY_VALUE ? "text-gray-400" : "text-gray-900"
                                                        }`}
                                                    >
                                                        {display}
                                                    </span>
                                                </td>
                                            );
                                        }

                                        if (column.id === "canonical_product_name") {
                                            const display = renderDefaultValue(item.canonical_product_name);
                                            return (
                                                <td
                                                    key={column.id}
                                                    className={`${commonCellClass} cursor-pointer overflow-hidden`}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onEditItem?.(item);
                                                    }}
                                                >
                                                    <span
                                                        className={`pointer-events-none block w-full text-left text-sm leading-snug break-words whitespace-normal ${
                                                            display === EMPTY_VALUE
                                                                ? "text-gray-400"
                                                                : "text-gray-900"
                                                        }`}
                                                    >
                                                        {display}
                                                    </span>
                                                </td>
                                            );
                                        }

                                        if (
                                            column.id === "amazon_fbm_sku" ||
                                            column.id === "amazon_fba_sku" ||
                                            column.id === "packaging_sku"
                                        ) {
                                            return (
                                                <td
                                                    key={column.id}
                                                    className={`${commonCellClass} overflow-hidden`}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                    }}
                                                >
                                                    {renderSkuValues(item[column.id])}
                                                </td>
                                            );
                                        }

                                        if (fillableColumns.has(column.id)) {
                                            const isActiveCell =
                                                activeCell?.rowIndex === index &&
                                                activeCell?.columnId === column.id;
                                            const isEditingCell =
                                                editingCell?.rowIndex === index &&
                                                editingCell?.columnId === column.id;
                                            const isDropdownEditable = dropdownEditableColumns.has(column.id);
                                            const isInputEditable = inputEditableColumns.has(column.id);
                                            const isInFillRange = Boolean(
                                                isDragFilling &&
                                                activeCell?.columnId === column.id &&
                                                dragFillRow !== null &&
                                                index >= Math.min(activeCell.rowIndex, dragFillRow) &&
                                                index <= Math.max(activeCell.rowIndex, dragFillRow)
                                            );
                                            const shownValue = column.id === "status"
                                                ? enumInventoryPhasedStatus[item.status] || renderDefaultValue(item.status)
                                                : column.id === "container_group_id"
                                                ? getContainerGroupLabel(item.container_group_id, item)
                                                : column.id === "lead_time_days"
                                                ? formatMfgLeadTimeTableDisplay(item.lead_time_days)
                                                : column.id === "transit_lead_time_days"
                                                ? formatTransitLeadTimeTableDisplay(item.transit_lead_time_days)
                                                : renderDefaultValue(item[column.id]);
                                            const plannedColumn =
                                                column.id === "shopify_planned_gross_price" ||
                                                column.id === "amazon_planned_gross_price";
                                            const plannedMeta = plannedColumn
                                                ? item[`${column.id}_meta`] || { status: "missing" }
                                                : null;
                                            const plannedDisplay = item[column.id] == null
                                                ? EMPTY_VALUE
                                                : formatCatalogMsrpDisplay(item[column.id]);

                                            return (
                                                <td
                                                    key={column.id}
                                                    ref={(node) => {
                                                        if (
                                                            editingCell?.rowIndex === index &&
                                                            editingCell?.columnId === column.id &&
                                                            dropdownEditableColumns.has(column.id)
                                                        ) {
                                                            dropdownAnchorRef.current = node;
                                                        }
                                                    }}
                                                    className={`${commonCellClass} relative cursor-cell overflow-hidden ${isActiveCell ? "ring-2 ring-inset ring-gray-900 bg-blue-50/30" : ""} ${isInFillRange && !isActiveCell ? "bg-blue-50/30 ring-1 ring-inset ring-blue-200 ring-dashed" : ""}`}
                                                    title={plannedColumn
                                                        ? `Source: ${plannedMeta?.source || "missing"} | Effective: ${formatPlannedPriceDate(plannedMeta?.effective_from)} | v${plannedMeta?.version || "n/a"} | by ${plannedMeta?.created_by || "n/a"} | ${plannedMeta?.note || ""}`
                                                        : undefined}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                    }}
                                                    onMouseDown={(event) => {
                                                        event.stopPropagation();
                                                        void commitInlineEditRef.current();
                                                        setActiveCell({ rowIndex: index, columnId: column.id });
                                                        setEditingCell(null);
                                                        setInlineEdit(null);
                                                    }}
                                                    onDoubleClick={(event) => {
                                                        if (isDropdownEditable) {
                                                            if (column.id === "supplier" && vendorSelectLoading) return;
                                                            event.stopPropagation();
                                                            setActiveCell({ rowIndex: index, columnId: column.id });
                                                            setEditingCell({ rowIndex: index, columnId: column.id });
                                                            return;
                                                        }
                                                        if (isInputEditable) {
                                                            event.stopPropagation();
                                                            setActiveCell({ rowIndex: index, columnId: column.id });
                                                            setEditingCell({ rowIndex: index, columnId: column.id });
                                                            const raw = item[column.id];
                                                            let v = raw === EMPTY_VALUE || raw === null || raw === undefined ? "" : String(raw);
                                                            if (integerInputColumns.has(column.id)) {
                                                                v = sanitizeUnsignedIntegerString(v);
                                                            }
                                                            setInlineEdit({ rowIndex: index, columnId: column.id, value: v });
                                                        }
                                                    }}
                                                    onMouseEnter={() => {
                                                        if (
                                                            isDragFilling &&
                                                            activeCell?.columnId === column.id &&
                                                            index !== activeCell.rowIndex
                                                        ) {
                                                            setDragFillRow(index);
                                                        }
                                                    }}
                                                >
                                                    {wrapContainerQtyCellContent(
                                                        dateInputColumns.has(column.id) ? (
                                                        <div
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            className="min-w-0 max-w-[13rem]"
                                                        >
                                                            <DatePickerInput
                                                                value={
                                                                    item[column.id] === EMPTY_VALUE ||
                                                                    item[column.id] === null ||
                                                                    item[column.id] === undefined
                                                                        ? ""
                                                                        : String(item[column.id]).slice(0, 10)
                                                                }
                                                                onChange={(iso) => {
                                                                    void onCellSelectValue(index, column.id, iso);
                                                                }}
                                                                placeholder="Pick a date"
                                                                className="w-full"
                                                                buttonClassName="!py-1 !px-2 !text-xs !whitespace-nowrap"
                                                            />
                                                        </div>
                                                    ) : isEditingCell && isDropdownEditable ? (
                                                        <div className="whitespace-normal break-words">
                                                            {plannedColumn ? (
                                                                <span className="inline-flex items-center gap-1">
                                                                    <span className={plannedMeta?.status === "missing" ? "text-amber-700" : ""}>
                                                                        {plannedDisplay}
                                                                    </span>
                                                                    <span className={`rounded px-1 py-0.5 text-[10px] font-medium ${plannedMeta?.status === "missing" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600"}`}>
                                                                        {plannedMeta?.status
                                                                            ? `${plannedMeta.status.charAt(0).toUpperCase()}${plannedMeta.status.slice(1)}`
                                                                            : "Missing"}
                                                                    </span>
                                                                </span>
                                                            ) : column.id === "status" ? (
                                                                <CatalogStatusChip status={item.status} label={shownValue} />
                                                            ) : (column.id === "sell_on_shopify" || column.id === "sell_on_amazon") ? (
                                                                renderBooleanMark(item[column.id])
                                                            ) : column.id === "shopify_product_id" ? (
                                                                <span className="">
                                                                    {formatShopifyGidForDisplay(item[column.id])}
                                                                </span>
                                                            ) : column.id === "lead_time_days" ? (
                                                                renderLeadTimeCellValue(column.id, shownValue)
                                                            ) : (
                                                                shownValue
                                                            )}
                                                        </div>
                                                    ) : isEditingCell && inlineEdit && inlineEdit.rowIndex === index && inlineEdit.columnId === column.id ? (
                                                        textareaEditableColumns.has(column.id) ? (
                                                            <textarea
                                                                autoComplete="off"
                                                                rows={4}
                                                                className="w-full min-w-[10rem] min-h-[5rem] rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-800 resize-y"
                                                                value={inlineEdit.value}
                                                                onChange={(e) => {
                                                                    const next = e.target.value;
                                                                    setInlineEdit((prev) => {
                                                                        if (!prev || prev.rowIndex !== index || prev.columnId !== column.id) {
                                                                            return prev;
                                                                        }
                                                                        return { ...prev, value: next };
                                                                    });
                                                                }}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onBlur={() => {
                                                                    void commitInlineEditRef.current();
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                                                        e.preventDefault();
                                                                        e.currentTarget.blur();
                                                                    }
                                                                }}
                                                                autoFocus
                                                            />
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                inputMode={
                                                                    integerInputColumns.has(column.id)
                                                                        ? "numeric"
                                                                        : physicalInputColumns.has(column.id)
                                                                            ? "decimal"
                                                                            : undefined
                                                                }
                                                                autoComplete="off"
                                                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm text-gray-800"
                                                                value={inlineEdit.value}
                                                                onChange={(e) => {
                                                                    const next = e.target.value;
                                                                    setInlineEdit((prev) => {
                                                                        if (!prev || prev.rowIndex !== index || prev.columnId !== column.id) {
                                                                            return prev;
                                                                        }
                                                                        if (integerInputColumns.has(column.id)) {
                                                                            return { ...prev, value: sanitizeUnsignedIntegerString(next) };
                                                                        }
                                                                        return { ...prev, value: next };
                                                                    });
                                                                }}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onBlur={() => {
                                                                    void commitInlineEditRef.current();
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (integerInputColumns.has(column.id)) {
                                                                        blockUnsignedIntegerKeyDown(e);
                                                                    }
                                                                    if (e.key === "Enter") {
                                                                        e.currentTarget.blur();
                                                                    }
                                                                }}
                                                                autoFocus
                                                            />
                                                        )
                                                    ) : (
                                                        <div className="whitespace-normal break-words">
                                                            {column.id === "status" ? (
                                                                <CatalogStatusChip status={item.status} label={shownValue} />
                                                            ) : (column.id === "sell_on_shopify" || column.id === "sell_on_amazon") ? (
                                                                renderBooleanMark(item[column.id])
                                                            ) : column.id === "shopify_product_id" ? (
                                                                <span className="">
                                                                    {formatShopifyGidForDisplay(item[column.id])}
                                                                </span>
                                                            ) : column.id === "lead_time_days" ? (
                                                                renderLeadTimeCellValue(column.id, shownValue)
                                                            ) : (
                                                                shownValue
                                                            )}
                                                        </div>
                                                    ),
                                                    item,
                                                    column.id,
                                                    containerQtyHelpContext,
                                                    item?.id ?? `row-${index}`
                                                    )}
                                                    {isActiveCell && (
                                                        <div
                                                            className="absolute bottom-0 right-0 w-3 h-3 cursor-crosshair z-20"
                                                            style={{ transform: "translate(50%, 50%)" }}
                                                            onMouseDown={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                startFill(index, column.id);
                                                            }}
                                                        >
                                                            <div className="w-2.5 h-2.5 bg-gray-900 border-2 border-white rounded-sm" />
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        }

                                        if (column.id === "msrp") {
                                            const isManualMsrpEditable = item.msrp_editable === true;
                                            const isActiveCell =
                                                activeCell?.rowIndex === index &&
                                                activeCell?.columnId === column.id;
                                            const isEditingCell =
                                                editingCell?.rowIndex === index &&
                                                editingCell?.columnId === column.id;
                                            const shownValue = renderDefaultValue(item.msrp);

                                            if (!isManualMsrpEditable) {
                                                return (
                                                    <td
                                                        key={column.id}
                                                        className={`${commonCellClass} overflow-hidden`}
                                                        style={{
                                                            width: wPx,
                                                            minWidth: wPx,
                                                            maxWidth: wPx,
                                                            boxSizing: "border-box",
                                                            ...getPinnedStyles(column, pinnedOffsets),
                                                        }}
                                                        title="MSRP from Shopify"
                                                    >
                                                        <div className="whitespace-normal break-words">{shownValue}</div>
                                                    </td>
                                                );
                                            }

                                            return (
                                                <td
                                                    key={column.id}
                                                    className={`${commonCellClass} relative cursor-cell overflow-hidden ${isActiveCell ? "ring-2 ring-inset ring-gray-900 bg-blue-50/30" : ""}`}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                    }}
                                                    title="Manual MSRP (Shopify MSRP missing)"
                                                    onMouseDown={(event) => {
                                                        event.stopPropagation();
                                                        void commitInlineEditRef.current();
                                                        setActiveCell({ rowIndex: index, columnId: column.id });
                                                        setEditingCell(null);
                                                        setInlineEdit(null);
                                                    }}
                                                    onDoubleClick={(event) => {
                                                        event.stopPropagation();
                                                        setActiveCell({ rowIndex: index, columnId: column.id });
                                                        setEditingCell({ rowIndex: index, columnId: column.id });
                                                        const raw =
                                                            item.manual_msrp != null &&
                                                            String(item.manual_msrp).trim() !== ""
                                                                ? String(item.manual_msrp)
                                                                : item.msrp === EMPTY_VALUE
                                                                  ? ""
                                                                  : String(item.msrp).replace(/[^0-9.-]/g, "");
                                                        setInlineEdit({
                                                            rowIndex: index,
                                                            columnId: column.id,
                                                            value: raw,
                                                        });
                                                    }}
                                                >
                                                    {isEditingCell &&
                                                    inlineEdit &&
                                                    inlineEdit.rowIndex === index &&
                                                    inlineEdit.columnId === column.id ? (
                                                        <input
                                                            autoComplete="off"
                                                            className="w-full min-w-[4.5rem] rounded border border-gray-300 px-2 py-1 text-sm text-gray-800"
                                                            value={inlineEdit.value}
                                                            onChange={(e) => {
                                                                const next = e.target.value;
                                                                setInlineEdit((prev) => {
                                                                    if (
                                                                        !prev ||
                                                                        prev.rowIndex !== index ||
                                                                        prev.columnId !== column.id
                                                                    ) {
                                                                        return prev;
                                                                    }
                                                                    return { ...prev, value: next };
                                                                });
                                                            }}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onBlur={() => {
                                                                void commitInlineEditRef.current();
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    e.currentTarget.blur();
                                                                }
                                                            }}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <div className="whitespace-normal break-words">{shownValue}</div>
                                                    )}
                                                </td>
                                            );
                                        }

                                        if (column.id === "total_lead_time_days") {
                                            return (
                                                <td
                                                    key={column.id}
                                                    className={`${commonCellClass} overflow-hidden`}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                    }}
                                                >
                                                    <div className="flex min-w-0 items-center justify-between gap-1">
                                                        <span className="min-w-0 whitespace-normal break-words">
                                                            {formatTotalLeadTimeTableDisplay(item)}
                                                        </span>
                                                        <CatalogInfoTip text={CATALOG_TOTAL_LEAD_TIME_INFO} />
                                                    </div>
                                                </td>
                                            );
                                        }

                                        if (
                                            column.id === "is_protected"
                                            || column.id === "max_discount_pct"
                                            || column.id === "max_discount_price"
                                        ) {
                                            const displayValue = column.id === "is_protected"
                                                ? item.is_protected === true
                                                    ? "Yes"
                                                    : item.is_protected === false
                                                      ? "No"
                                                      : EMPTY_VALUE
                                                : column.id === "max_discount_pct"
                                                  ? item.max_discount_pct == null
                                                      ? EMPTY_VALUE
                                                      : `${item.max_discount_pct}%`
                                                  : formatCatalogMsrpDisplay(item.max_discount_price);
                                            return (
                                                <td
                                                    key={column.id}
                                                    className={`${commonCellClass} overflow-hidden`}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                    }}
                                                >
                                                    <div className="whitespace-normal break-words">{displayValue}</div>
                                                </td>
                                            );
                                        }

                                        if (column.id === "status") {
                                            const status = item.status;
                                            return (
                                                <td
                                                    key={column.id}
                                                    className={`${commonCellClass} overflow-hidden`}
                                                    style={{
                                                        width: wPx,
                                                        minWidth: wPx,
                                                        maxWidth: wPx,
                                                        boxSizing: "border-box",
                                                        ...getPinnedStyles(column, pinnedOffsets),
                                                    }}
                                                >
                                                    <CatalogStatusChip
                                                        status={status}
                                                        label={enumInventoryPhasedStatus[status] || renderDefaultValue(status)}
                                                    />
                                                </td>
                                            );
                                        }

                                        return (
                                            <td
                                                key={column.id}
                                                className={`${commonCellClass} overflow-hidden`}
                                                style={{
                                                    width: wPx,
                                                    minWidth: wPx,
                                                    maxWidth: wPx,
                                                    boxSizing: "border-box",
                                                    ...getPinnedStyles(column, pinnedOffsets),
                                                }}
                                            >
                                                <div className="whitespace-normal break-words">
                                                    {(column.id === "sell_on_shopify" || column.id === "sell_on_amazon")
                                                        ? renderBooleanMark(item[column.id])
                                                        : column.id === "shopify_product_id" ? (
                                                            <span className="">
                                                                {formatShopifyGidForDisplay(item[column.id])}
                                                            </span>
                                                        ) : (
                                                        CATALOG_TABLE_DATE_DISPLAY_COLUMNS.has(column.id)
                                                          ? formatCatalogTableDateDisplay(item[column.id])
                                                          : renderDefaultValue(item[column.id]))}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {/* Mobile (<640px): table collapses to a stacked card list */}
            <div
                className={`catalog-table-scroll overflow-y-auto sm:hidden ${useToolbarMeasuredMaxHeight ? "" : "max-h-[calc(100vh-220px)]"}`}
                style={scrollMaxHeightStyle}
            >
                {tableItems.map((item) => {
                    const rowSelected = selectedRowIds.has(item.id);
                    const skuDisplay = renderDefaultValue(item.supply_sku);
                    const colorDisplay = renderDefaultValue(item.color);
                    const categoryDisplay = renderDefaultValue(item.category);
                    const subcategoryDisplay = renderDefaultValue(item.subcategory);
                    const categoryLine = [categoryDisplay, subcategoryDisplay]
                        .filter((value) => value !== EMPTY_VALUE)
                        .join(" · ");
                    return (
                        <div
                            key={item.id}
                            className={`flex cursor-pointer items-start gap-3 border-b border-gray-100 px-4 py-3 transition-colors ${rowSelected ? "bg-gray-50" : ""}`}
                            onClick={() => onEditItem?.(item)}
                        >
                            <input
                                type="checkbox"
                                className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 accent-primary focus:ring-primary focus:ring-offset-0"
                                checked={rowSelected}
                                onChange={() => toggleRowSelected(item.id)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Select ${item.supply_sku || item.id}`}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 items-start justify-between gap-2">
                                    <span className="min-w-0 break-words text-[13px] font-bold leading-tight text-gray-900">
                                        {renderDefaultValue(item.canonical_product_name)}
                                    </span>
                                    <CatalogStatusChip
                                        status={item.status}
                                        label={enumInventoryPhasedStatus[item.status] || renderDefaultValue(item.status)}
                                    />
                                </div>
                                <div className="mt-1 flex min-w-0 items-center gap-2">
                                    <SupplySku
                                        sku={skuDisplay}
                                        className="block max-w-full shrink-0 truncate text-left"
                                        title={skuDisplay}
                                    />
                                    {colorDisplay !== EMPTY_VALUE ? (
                                        <span className="min-w-0 truncate text-[12px] text-gray-500">{colorDisplay}</span>
                                    ) : null}
                                </div>
                                {categoryLine ? (
                                    <div className="mt-1 truncate text-[11px] text-gray-500">{categoryLine}</div>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>
            {dropdownPortal}
            {isLoading && (
                <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px]">
                    <LoaderSpinner width="56" height="56" border="4" colorSpinner="black" detached />
                </div>
            )}
        </div>
        <ConfrimModal
            isOpen={bulkEditConfirmOpen}
            isLoading={isBulkUpdating}
            title={pendingBulkEdit?.title || ""}
            confirmButtonTitle="Apply"
            submitClick={confirmBulkEditApply}
            cancelClick={cancelBulkEditConfirm}
            size="max-w-lg"
            testId="product-catalog-bulk-edit-confirm"
        />
        <ProductCatalogBulkSectionModals
            openSection={bulkSectionModal}
            onClose={() => setBulkSectionModal(null)}
            selectedCount={selectedCount}
            productIds={selectedIdList}
            statusOptions={statusOptions}
            vendorSelectOptions={vendorSelectOptions}
            vendorSelectLoading={vendorSelectLoading}
            containerGroupOptions={containerGroupOptions}
            onApply={onBulkSectionApply}
            disabled={isBulkUpdating || !onBulkSectionApply}
        />
        </>
    );
});

ProductCatalogTable.displayName = "ProductCatalogTable";
